import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { useJourneyApi } from '../journey-api-context';
import { JourneyCustomersPage, JourneyFilterState } from '../../../../models/journey-explorer/journey-explorer';
import { errorMessage, formatNumber, formatRelative, isoToUkDate } from '../journey-explorer-utils';
import ExportCustomers from './export-customers';
const PAGE_SIZE = 25;
const LOAD_ERROR = 'The customers on this path could not be loaded.';

interface CustomerDrilldownProps {
	filters: JourneyFilterState;
	pathKey: string;
	onOpenCustomer: (customerId: string) => void;
}

const CustomerDrilldown: FC<CustomerDrilldownProps> = ({ filters, pathKey, onOpenCustomer }) => {
	const apiService = useJourneyApi();
	const apiRef = useRef(apiService);
	apiRef.current = apiService;

	const [page, setPage] = useState(1);
	const [data, setData] = useState<JourneyCustomersPage | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		const controller = new AbortController();
		setLoading(true);
		setError(null);
		apiRef.current
			.getCustomers({ filters, path_key: pathKey, page, page_size: PAGE_SIZE }, controller.signal)
			.then(setData)
			.catch((reason) => !controller.signal.aborted && setError(errorMessage(reason, LOAD_ERROR)))
			.finally(() => !controller.signal.aborted && setLoading(false));
		return () => controller.abort();
	}, [filters, pathKey, page, attempt]);

	const attributeLabels = useMemo(
		() => Array.from(new Set((data?.customers ?? []).flatMap((customer) => Object.keys(customer.attributes)))),
		[data]
	);

	const gridTemplateColumns = `1.1fr 1fr ${attributeLabels.map(() => '1.1fr ').join('')}0.9fr 1fr`;
	const total = data?.total ?? 0;
	const firstRow = total ? (page - 1) * PAGE_SIZE + 1 : 0;
	const lastRow = Math.min(page * PAGE_SIZE, total);
	const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

	if (error) {
		return (
			<div className={styles.drilldown}>
				<div className={styles.drilldown_message}>
					{error}
					<button type="button" className={styles.retry} onClick={() => setAttempt((value) => value + 1)}>
						Retry
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className={`${styles.drilldown} ${loading && data ? styles.loading : ''}`}>
			{!data ? (
				<div className={styles.drilldown_message}>
					<span className={styles.spinner} aria-label="Loading" />
				</div>
			) : (
				<>
					<div className={`${styles.drilldown_row} ${styles.drilldown_head}`} style={{ gridTemplateColumns }}>
						<span>Customer</span>
						<span>Start date</span>
						{attributeLabels.map((label) => (
							<span key={label}>{label}</span>
						))}
						<span>Last seen</span>
						<span>Goal reached</span>
					</div>
					{data.customers.map((customer) => (
						<button
							type="button"
							key={customer.customer_id}
							className={`${styles.drilldown_row} ${styles.drilldown_row_clickable}`}
							style={{ gridTemplateColumns }}
							title="See this customer's full journey"
							onClick={() => onOpenCustomer(customer.customer_id)}
						>
							<span className={styles.drilldown_id}>{customer.customer_id}</span>
							<span className={styles.drilldown_muted}>
								{customer.start_date ? isoToUkDate(customer.start_date) : ''}
							</span>
							{attributeLabels.map((label) => (
								<span key={label} className={styles.drilldown_muted}>
									{customer.attributes[label] ?? ''}
								</span>
							))}
							<span className={styles.drilldown_dim}>
								{customer.last_seen ? formatRelative(customer.last_seen) : ''}
							</span>
							<span className={customer.goal_date ? styles.goal_text : styles.drilldown_muted}>
								{customer.goal_date ? isoToUkDate(customer.goal_date) : 'Not reached'}
							</span>
						</button>
					))}
					<div className={styles.drilldown_footer}>
						<span>
							Showing {formatNumber(firstRow)}–{formatNumber(lastRow)} of {formatNumber(total)} customers
						</span>
						<ExportCustomers filters={filters} pathKey={pathKey} customers={total} compact />
						{pageCount > 1 && (
							<span className={styles.pager}>
								<button
									type="button"
									className={styles.link}
									disabled={page <= 1 || loading}
									onClick={() => setPage((value) => value - 1)}
								>
									Previous
								</button>
								<span>
									Page {page} of {pageCount}
								</span>
								<button
									type="button"
									className={styles.link}
									disabled={page >= pageCount || loading}
									onClick={() => setPage((value) => value + 1)}
								>
									Next
								</button>
							</span>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default CustomerDrilldown;
